import { useMemo, useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { useForm } from 'react-hook-form'
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import { Plus } from 'lucide-react'
import { ConfirmAction } from '@/components/confirm-action'
import { DataTable } from '@/components/data-table'
import { Notice, PageHeader } from '@/components/page'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardDescription } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useSession } from '@/features/auth/use-session'
import {
  ADMIN_WRITES_ENABLED,
  grantSchema,
  isLastSuperAdmin,
  ROLE_LABEL,
  useAdmins,
  useGrantRole,
  useUpdateAdmin,
  type AdminRow,
  type GrantInput,
  type OpsRoleCode,
} from '@/features/admins/api'
import { localDate } from '@/lib/datetime'

type PendingChange =
  | { kind: 'revoke'; admin: AdminRow }
  | { kind: 'role'; admin: AdminRow; to: OpsRoleCode }

export function AdminsPage() {
  const { user } = useSession()
  const query = useAdmins()
  const update = useUpdateAdmin()

  const [granting, setGranting] = useState(false)
  const [pending, setPending] = useState<PendingChange | null>(null)

  const columns = useMemo<ColumnDef<AdminRow, unknown>[]>(
    () => [
      {
        accessorKey: 'display_name',
        header: 'Admin',
        cell: ({ row }) => (
          <div className="flex items-center gap-2.5">
            <div className="grid size-8 shrink-0 place-items-center rounded-full bg-primary-soft text-xs font-bold text-accent-foreground">
              {row.original.display_name.charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="font-semibold">{row.original.display_name}</div>
              <div className="text-xs text-faint">{row.original.email}</div>
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'role',
        header: 'Role',
        cell: ({ row }) => (
          <Badge variant={row.original.role === 'super_admin' ? 'accent' : 'default'}>
            {ROLE_LABEL[row.original.role]}
          </Badge>
        ),
      },
      {
        accessorKey: 'granted_at',
        header: 'Added',
        cell: ({ row }) => (
          <div>
            <div>{localDate(row.original.granted_at)}</div>
            <div className="text-xs text-faint">
              {row.original.granted_by ? `by ${row.original.granted_by}` : 'seeded by hand'}
            </div>
          </div>
        ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const admin = row.original
          const isSelf = admin.id === user?.id

          if (isSelf) {
            return (
              <div className="flex justify-end">
                <Badge variant="default">You</Badge>
              </div>
            )
          }

          // Read-only until the write endpoints are mounted. The "You" badge
          // above still renders — it identifies the signed-in row and needs no
          // backend.
          if (!ADMIN_WRITES_ENABLED) return null

          const isLastSuper = isLastSuperAdmin(query.data, admin.id)

          const otherRole: OpsRoleCode =
            admin.role === 'super_admin' ? 'ops_admin' : 'super_admin'

          return (
            <div className="flex justify-end gap-2 whitespace-nowrap">
              <Button
                variant="secondary"
                size="sm"
                disabled={isLastSuper || update.isPending}
                title={
                  isLastSuper ? 'The last remaining Super Admin cannot be downgraded' : undefined
                }
                onClick={() => setPending({ kind: 'role', admin, to: otherRole })}
              >
                Make {ROLE_LABEL[otherRole].replace(' Admin', '')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={isLastSuper || update.isPending}
                title={isLastSuper ? 'The last remaining Super Admin cannot be revoked' : undefined}
                onClick={() => setPending({ kind: 'revoke', admin })}
              >
                Revoke
              </Button>
            </div>
          )
        },
      },
    ],
    [query.data, update.isPending, user?.id],
  )

  return (
    <>
      <PageHeader
        title="Admins & Roles"
        actions={
          ADMIN_WRITES_ENABLED ? (
            <Button onClick={() => setGranting(true)}>
              <Plus />
              Grant role
            </Button>
          ) : undefined
        }
      />

      <Notice>
        <b>Super Admin only.</b> Operations Admins cannot see this screen. Super Admins have full
        authority — they grant and revoke both roles. A Super Admin <b>cannot</b> revoke or downgrade
        the last remaining Super Admin.
      </Notice>

      {!ADMIN_WRITES_ENABLED && (
        <Notice>
          <b>Viewing only, for now.</b> This screen reads the live role assignments. Granting and
          revoking are specified and built, but not switched on: revoking has to guarantee the last
          Super Admin cannot lose the role even when two Super Admins act at the same moment, and
          getting that wrong locks everyone out of the console with no way back in from the UI.
        </Notice>
      )}

      <Card>
        <CardDescription className="mb-3">
          A role is granted to someone who has <b>already signed in once</b> through the normal
          consumer flow — there are no invite emails. The <i>first</i> Super Admin is seeded by hand
          in the database; there is no in-app path to create it.
        </CardDescription>

        <DataTable
          columns={columns}
          query={query}
          emptyMessage="No roles granted yet."
        />
      </Card>

      {ADMIN_WRITES_ENABLED && <GrantDialog open={granting} onOpenChange={setGranting} />}

      {pending && (
        <ConfirmAction
          open
          onOpenChange={(open) => !open && setPending(null)}
          title={pending.kind === 'revoke' ? 'Revoke this role?' : 'Change this role?'}
          description={
            pending.kind === 'revoke' ? (
              <>
                <b>{pending.admin.display_name}</b> will lose console access immediately. Their
                consumer account and session are unaffected, and the record is kept for history.
              </>
            ) : (
              <>
                <b>{pending.admin.display_name}</b> becomes a{' '}
                <b>{ROLE_LABEL[pending.to]}</b>. This takes effect on their next request — no
                sign-out needed.
              </>
            )
          }
          confirmLabel={pending.kind === 'revoke' ? 'Revoke' : 'Change role'}
          destructive={pending.kind === 'revoke'}
          onConfirm={() =>
            update.mutateAsync(
              pending.kind === 'revoke'
                ? { id: pending.admin.id, revoke: true }
                : { id: pending.admin.id, role: pending.to },
            )
          }
          onConflict={() => void query.refetch()}
        />
      )}
    </>
  )
}

function GrantDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const grant = useGrantRole()
  const form = useForm<GrantInput>({
    resolver: standardSchemaResolver(grantSchema),
    defaultValues: { email: '', role: 'ops_admin' },
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          form.reset()
          grant.reset()
        }
        onOpenChange(next)
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Grant a console role</DialogTitle>
          <DialogDescription>
            Matched on the email of an existing account. If they have never signed in, ask them to
            do that first — no invite is sent.
          </DialogDescription>
        </DialogHeader>

        <form
          id="grant-form"
          className="space-y-4"
          onSubmit={form.handleSubmit((values) =>
            grant.mutate(values, { onSuccess: () => onOpenChange(false) }),
          )}
        >
          <div className="space-y-2">
            <Label htmlFor="grant-email">Email</Label>
            <Input id="grant-email" type="email" placeholder="name@coinworldcap.com" {...form.register('email')} />
            {form.formState.errors.email && (
              <p className="text-xs text-destructive">{form.formState.errors.email.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="grant-role">Role</Label>
            <Select
              value={form.watch('role')}
              onValueChange={(value) => form.setValue('role', value as OpsRoleCode)}
            >
              <SelectTrigger id="grant-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ops_admin">Operations Admin</SelectItem>
                <SelectItem value="super_admin">Super Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {grant.isError && (
            <p className="text-xs text-destructive">{(grant.error as Error).message}</p>
          )}
        </form>

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form="grant-form" disabled={grant.isPending}>
            {grant.isPending ? 'Granting…' : 'Grant role'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
